import { formatCents } from './money.js';
import { WEEKDAY_NAMES_ES, MONTH_NAMES_ES, dateLabel, parts } from './dates.js';
import { versionSegments } from './schedule.js';
export const BEHAVIOR_LABELS = {
    budget: 'Presupuesto que se consume',
    fixed: 'Gasto fijo recurrente',
    variable_service: 'Servicio recurrente variable',
    occurrence: 'Gasto por ocurrencias',
    one_off: 'Gasto puntual',
    income: 'Ingreso',
    loan: 'Préstamo recibido',
};
export const BEHAVIOR_HELP = {
    budget: 'Cantidad reservada por período que se consume progresivamente. Mientras el mes está abierto se proyecta el máximo entre dotación y consumo; al cerrar se usa el real y el remanente queda en el ahorro.',
    fixed: 'Obligación con importe previsto por vencimiento. El cargo real vinculado cubre el vencimiento y no suma un segundo gasto.',
    variable_service: 'Estimación por factura hasta que llega el importe real, que la reemplaza tanto si sube como si baja.',
    occurrence: 'Precio por sesión o evento y calendario. El total del mes depende de cuántas ocurrencias caen en él, con sus excepciones.',
    one_off: 'Gasto único, opcionalmente financiado en cuotas mensuales que conservan el concepto.',
    income: 'Ingreso recurrente o extraordinario. Se distingue el mes trabajado, el cobro y el mes que financia.',
    loan: 'Entrada de financiación separada del ahorro generado, con calendario de devolución que afecta al disponible.',
};
export const FREQUENCY_LABELS = {
    weekly: 'Semanal',
    biweekly: 'Cada 14 días',
    monthly: 'Mensual',
    bimonthly: 'Bimestral',
    annual: 'Anual',
    once: 'Puntual',
    weekend: 'Por fin de semana',
};
export function weekdayName(weekday) {
    return WEEKDAY_NAMES_ES[weekday - 1] ?? '';
}
/** Descripción legible del calendario de una versión. */
export function describeSchedule(frequency, schedule, validFrom) {
    switch (frequency) {
        case 'weekly': {
            const wd = schedule.weekday ?? (() => {
                const d = schedule.referenceDate ?? validFrom;
                const js = new Date(`${d}T00:00:00Z`).getUTCDay();
                return js === 0 ? 7 : js;
            })();
            return `Cada ${weekdayName(wd)}`;
        }
        case 'biweekly':
            return `Cada 14 días desde el ${dateLabel(schedule.referenceDate ?? validFrom)}`;
        case 'monthly':
            return `Una vez al mes (día ${schedule.dayOfMonth ?? parts(schedule.referenceDate ?? validFrom).day} orientativo)`;
        case 'bimonthly':
            return `Cada dos meses desde ${MONTH_NAMES_ES[parts(schedule.referenceDate ?? validFrom).month - 1]}, día ${schedule.dayOfMonth ?? parts(schedule.referenceDate ?? validFrom).day}`;
        case 'annual': {
            const ref = schedule.referenceDate ?? validFrom;
            const month = schedule.monthOfYear ?? parts(ref).month;
            const day = schedule.dayOfMonth ?? parts(ref).day;
            return `Cada año, el ${day} de ${MONTH_NAMES_ES[month - 1]}`;
        }
        case 'once': {
            const date = schedule.referenceDate ?? validFrom;
            const n = schedule.instalments ?? 1;
            return n > 1 ? `${n} cuotas mensuales desde el ${dateLabel(date)}` : `Una vez, el ${dateLabel(date)}`;
        }
        case 'weekend':
            return 'Dotación por cada fin de semana del mes';
        case null:
            return 'Sin calendario';
    }
}
export function describeAmount(version, behavior) {
    if (version.amountCents === null)
        return 'Sin importe';
    const amount = formatCents(version.amountCents);
    switch (behavior) {
        case 'budget':
            return version.frequency === 'weekend' ? `${amount} por fin de semana` : `${amount} por mes`;
        case 'occurrence':
            return `${amount} por sesión`;
        case 'variable_service':
            return `${amount} estimados por factura`;
        case 'loan':
            return `${amount} por cuota${version.principalCents ? ` · recibido ${formatCents(version.principalCents)}` : ''}`;
        case 'one_off':
            return version.schedule.instalments && version.schedule.instalments > 1 ? `${amount} por cuota` : amount;
        default:
            return `${amount} por vencimiento`;
    }
}
/** Línea de tiempo legible de las condiciones de un concepto (ficha de reglas). */
export function describeTimeline(behavior, versions) {
    return versionSegments(versions).map(({ version, from, to }) => {
        if (version.state === 'inactive') {
            return { version, from, to, title: 'Baja', details: [version.note ?? 'Sin previsión mientras esté dado de baja; el histórico se conserva.'] };
        }
        const details = [describeAmount(version, behavior), describeSchedule(version.frequency, version.schedule, version.validFrom)];
        if (version.schedule.periodOffset) {
            const off = version.schedule.periodOffset;
            details.push(off > 0 ? `Se imputa ${off} mes${off > 1 ? 'es' : ''} después de la fecha (mes financiado)` : `Se imputa ${-off} mes${off < -1 ? 'es' : ''} antes de la fecha`);
        }
        if (version.note)
            details.push(version.note);
        return { version, from, to, title: versions.indexOf(version) === 0 ? 'Alta' : 'Condiciones', details };
    });
}
//# sourceMappingURL=describe.js.map